import { Game } from "../../game.js";
import { markCanvasHasContext } from "../dom_backend/dom_backend.js";

/**
 * A page canvas placed right under a screen canvas, for the part of a screen
 * that animates behind its content (offer confetti). The compositor stacks the
 * two, so a confetti frame repaints only this layer and the content canvas is
 * touched only when the content itself changes. The earlier approach copied a
 * cached content bitmap back every frame, and on device each rebuild of that
 * bitmap blocked the main thread ~60 ms (canvas-to-canvas copies are
 * synchronous there); stacking layers needs no copy at all.
 */
export class BackdropLayer
{
  public readonly canvas: HTMLCanvasElement;
  public readonly ctx: CanvasRenderingContext2D;

  private constructor( canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D )
  {
    this.canvas = canvas;
    this.ctx = ctx;
  }

  /** Creates the layer under `front`, matching its placement and backing size. */
  public static under( front: HTMLCanvasElement ): BackdropLayer | null
  {
    const parent = front.parentNode;
    if ( !parent ) return null;

    const canvas = document.createElement( "canvas" );
    canvas.id = `${front.id || "screen"}Backdrop`;
    const style = canvas.style;
    style.left = front.style.left;
    style.top = front.style.top;
    style.width = front.style.width;
    style.height = front.style.height;
    style.pointerEvents = "none";
    style.display = "block";
    parent.insertBefore( canvas, front );

    // Same size and dpr base transform as the screen canvas it sits under.
    markCanvasHasContext( canvas );
    Game.instance.allocateScreenCanvas( canvas );
    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
    {
      canvas.remove();
      return null;
    }
    return new BackdropLayer( canvas, ctx );
  }

  public destroy(): void
  {
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.canvas.remove();
  }
}
